import { useState, useEffect, useCallback } from "react";
import {
  MessageCircle,
  Phone,
  Clock,
  AlertCircle,
  CheckCircle,
  UserPlus,
  Link,
  Ban,
  RefreshCw,
  ChevronRight,
  X,
  ArrowLeft,
  Mail,
  Globe,
  MessageSquare,
  ExternalLink,
  UserCheck,
  UserMinus,
  Users,
  Search,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Channel configuration ─────────────────────────────────────────────────────
const CHANNEL_CONFIG = {
  WHATSAPP: { label: "WhatsApp", Icon: MessageSquare, color: "#25D366", bg: "#F0FDF4" },
  EMAIL:    { label: "Email",    Icon: Mail,          color: "#EA4335", bg: "#FEF2F2" },
  WEB:      { label: "Web",      Icon: Globe,         color: "#7A1A2B", bg: "#FDF2F5" },
};

// ── Provider mode badges ──────────────────────────────────────────────────────
const PROVIDER_BADGES = {
  MOCK:       { label: "MOCK · SIMULATED", bg: "#FEF9C3", text: "#92400E", border: "#FDE047" },
  UAT_ACTIVE: { label: "UAT",              bg: "#FDF2F5", text: "#7A1A2B", border: "#C9A15F" },
  ACTIVE:     { label: "LIVE",             bg: "#DCFCE7", text: "#166534", border: "#86EFAC" },
  LIVE:       { label: "LIVE",             bg: "#DCFCE7", text: "#166534", border: "#86EFAC" },
};

function ProviderModeBadge({ mode }) {
  const cfg = PROVIDER_BADGES[mode] || PROVIDER_BADGES.MOCK;
  return (
    <span
      className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
      style={{ background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}
    >
      {cfg.label}
    </span>
  );
}

const STATUS_CONFIG = {
  UNMATCHED: {
    label: "Unmatched",
    badge: "bg-[#FFF3E0] text-[#E65100] border border-[#FFCC80]",
    dot: "bg-orange-400",
  },
  RESOLUTION_REQUIRED: {
    label: "Resolution Required",
    badge: "bg-[#FFFDE7] text-[#F57F17] border border-[#FFF176]",
    dot: "bg-yellow-400",
  },
  ASSIGNED: {
    label: "Assigned",
    badge: "bg-[#EEF4FF] text-[#0176D3] border border-[#C9DBF2]",
    dot: "bg-blue-500",
  },
  SPAM: {
    label: "Spam / Ignored",
    badge: "bg-[#F3F3F3] text-[#706E6B] border border-[#E5E5E5]",
    dot: "bg-gray-400",
  },
  RESOLVED: {
    label: "Resolved",
    badge: "bg-[#EEF9EE] text-[#2E7D32] border border-[#A5D6A7]",
    dot: "bg-green-500",
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.UNMATCHED;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${cfg.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function formatRelativeTime(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return "";
  }
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

// ── Message thread inside an inbox item ───────────────────────────────────────
function InboxMessageThread({ messages }) {
  if (!messages?.length) {
    return <p className="text-xs text-[#706E6B] py-4 text-center">No messages loaded</p>;
  }
  return (
    <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
      {messages.map((msg, i) => {
        const isOut = msg.direction === "OUTBOUND";
        return (
          <div key={i} className={`flex gap-2 ${isOut ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] px-3 py-2 rounded-lg text-xs ${
                isOut
                  ? "bg-[#0176D3] text-white rounded-br-none"
                  : "bg-[#F3F3F3] text-[#181818] border border-[#E5E5E5] rounded-bl-none"
              }`}
            >
              <p className="whitespace-pre-wrap break-words">{msg.original_content}</p>
              <span className="block text-right mt-0.5 opacity-60 text-[10px]">
                {formatDateTime(msg.occurred_at)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── B3: Link Lead modal ───────────────────────────────────────────────────────
function LinkLeadModal({ item, onClose, onSuccess }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("token");

  // Auto-populate with candidates if RESOLUTION_REQUIRED
  useEffect(() => {
    if (item.candidate_lead_ids?.length) {
      // Fetch candidate lead details
      Promise.all(
        item.candidate_lead_ids.slice(0, 5).map((id) =>
          fetch(`${API}/api/communication/leads/search?q=${encodeURIComponent(id)}&limit=5`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "include",
          }).then((r) => (r.ok ? r.json() : []))
        )
      )
        .then((all) => {
          const flat = all.flat();
          const seen = new Set();
          const deduped = flat.filter((l) => {
            if (seen.has(l.id)) return false;
            seen.add(l.id);
            return true;
          });
          setResults(deduped);
        })
        .catch(() => {});
    }
  }, [item, token]);

  const handleSearch = useCallback(() => {
    if (!query.trim()) return;
    setSearching(true);
    fetch(`${API}/api/communication/leads/search?q=${encodeURIComponent(query.trim())}&limit=10`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : []))
      .then(setResults)
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [query, token]);

  const handleLink = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/communication/inbox/${item.id}/link-lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ lead_id: selected.id }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to link lead");
      }
      const data = await r.json();
      onSuccess(data, selected);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="link-lead-modal"
    >
      <div className="bg-white border border-[#E5E5E5] rounded-xl w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
          <h3 className="text-base font-semibold text-[#181818]">Link to Existing Lead</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818] p-1 rounded hover:bg-[#F3F3F3]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {/* Phone context */}
          <div className="bg-[#F3F3F3] rounded-lg p-3 text-sm">
            <div className="flex items-center gap-2 text-[#706E6B]">
              <Phone className="w-3.5 h-3.5" />
              <span className="font-mono text-[#181818] font-medium">{item.phone_display || item.phone_normalized}</span>
            </div>
            {item.status === "RESOLUTION_REQUIRED" && item.candidate_lead_ids?.length > 0 && (
              <p className="text-xs text-orange-600 mt-1">
                {item.candidate_lead_ids.length} candidate lead{item.candidate_lead_ids.length !== 1 ? "s" : ""} found — select the correct one
              </p>
            )}
          </div>

          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-[#444444] mb-1">Search leads</label>
            <div className="flex gap-2">
              <input
                data-testid="link-lead-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Name, lead number, or phone…"
                className="flex-1 border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]"
              />
              <button
                data-testid="link-lead-search-btn"
                onClick={handleSearch}
                disabled={searching || !query.trim()}
                className="px-3 py-2 bg-[#F3F3F3] hover:bg-[#E5E5E5] text-[#444444] text-sm rounded-lg border border-[#E5E5E5] disabled:opacity-50"
              >
                {searching ? <Clock className="w-4 h-4 animate-spin" /> : "Search"}
              </button>
            </div>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="link-lead-results">
              {results.map((lead) => (
                <button
                  key={lead.id}
                  data-testid={`lead-result-${lead.id}`}
                  onClick={() => setSelected(lead)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                    selected?.id === lead.id
                      ? "border-[#0176D3] bg-[#EEF4FF]"
                      : "border-[#E5E5E5] hover:bg-[#F3F3F3]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="font-semibold text-[#181818]">{lead.customer_name}</span>
                      <span className="text-[#706E6B] ml-2 text-xs">{lead.lead_number}</span>
                    </div>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      lead.status === "ACTIVE" || lead.status === "QUALIFIED"
                        ? "bg-green-50 text-green-700"
                        : "bg-[#F3F3F3] text-[#706E6B]"
                    }`}>
                      {lead.status}
                    </span>
                  </div>
                  {lead.customer_phone && (
                    <span className="text-[11px] font-mono text-[#706E6B] mt-0.5 block">{lead.customer_phone}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {results.length === 0 && query.trim() && !searching && (
            <p className="text-xs text-[#706E6B] text-center py-3">No leads found for "{query}"</p>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              <AlertCircle className="w-3.5 h-3.5" />
              {error}
            </div>
          )}
        </div>
        <div className="flex gap-2 p-4 border-t border-[#E5E5E5]">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-sm text-[#706E6B] border border-[#E5E5E5] rounded-lg hover:bg-[#F3F3F3] transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="link-lead-confirm-btn"
            onClick={handleLink}
            disabled={!selected || submitting}
            className="flex-1 px-3 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#005FB2] disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
          >
            {submitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link className="w-3.5 h-3.5" />}
            {submitting ? "Linking…" : `Link${selected ? ` to ${selected.lead_number}` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
function CreateLeadModal({ item, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!name.trim()) { setError("Customer name is required"); return; }
    const token = localStorage.getItem("token");
    setSubmitting(true);
    try {
      const r = await fetch(
        `${API}/api/communication/inbox/${item.id}/create-lead`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ customer_name: name.trim() }),
        }
      );
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || "Failed to create lead");
      }
      const data = await r.json();
      onSuccess(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="create-lead-modal"
    >
      <div className="bg-white border border-[#E5E5E5] rounded-xl w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
          <h3 className="text-base font-semibold text-[#181818]">Create Lead from WhatsApp</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818] p-1 rounded hover:bg-[#F3F3F3]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {/* Source info */}
          <div className="bg-[#F3F3F3] rounded-lg p-3 text-sm space-y-1">
            <div className="flex gap-2 text-[#706E6B] items-center">
              <Phone className="w-3.5 h-3.5" />
              <span className="font-mono text-[#181818] font-medium">{item.phone_display || item.phone_normalized}</span>
            </div>
            <p className="text-xs text-[#706E6B] ml-5 line-clamp-2">{item.first_message_preview}</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#444444] mb-1">
              Customer Name <span className="text-red-500">*</span>
            </label>
            <input
              data-testid="create-lead-name-input"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null); }}
              placeholder="Enter the customer's name"
              className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoFocus
            />
            <p className="text-[11px] text-[#706E6B] mt-1">
              Only factual fields (phone, channel) will be pre-populated. No event details will be invented.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              <AlertCircle className="w-3.5 h-3.5" />
              {error}
            </div>
          )}
        </div>
        <div className="flex gap-2 p-4 border-t border-[#E5E5E5]">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-sm text-[#706E6B] border border-[#E5E5E5] rounded-lg hover:bg-[#F3F3F3] transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="create-lead-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
            className="flex-1 px-3 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#005FB2] disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
          >
            {submitting ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <UserPlus className="w-3.5 h-3.5" />
            )}
            {submitting ? "Creating…" : "Create Lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Capability helper ─────────────────────────────────────────────────────────
function hasCapability(user, cap) {
  if (!user) return false;
  const caps = user.capabilities || [];
  if (caps.includes(cap)) return true;
  const parts = cap.split(".");
  for (let i = 0; i < parts.length; i++) {
    if (caps.includes(parts.slice(0, i + 1).join(".") + ".*")) return true;
  }
  return false;
}

// ── Assign-to modal (manager view) ───────────────────────────────────────────
function AssignToModal({ itemId, currentAssigneeId, token, onClose, onSuccess }) {
  const [staff, setStaff]       = useState([]);
  const [filter, setFilter]     = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote]         = useState("");
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    fetch(`${API}/api/communication/inbox/staff`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : []))
      .then(setStaff)
      .catch(() => setStaff([]))
      .finally(() => setLoading(false));
  }, [token]);

  const filtered = staff.filter((u) => {
    const q = filter.toLowerCase();
    return !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  const handleAssign = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/communication/inbox/${itemId}/assign`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ assignee_id: selected.id, note: note.trim() || undefined }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Assignment failed");
      }
      const data = await r.json();
      onSuccess(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const ROLE_LABELS = {
    TENANT_ADMIN: "Admin", CRM_MANAGER: "Sales Manager",
    RELATIONSHIP_MANAGER: "Client RM", BUSINESS_OWNER: "Business Owner",
    EVENT_PLANNER_DESIGNER: "Event Planner", PROJECT_MANAGER: "Event Manager",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="assign-to-modal">
      <div className="bg-white border border-[#E5E5E5] rounded-xl w-full max-w-sm mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-semibold text-[#181818]">Assign Conversation</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818] p-1 rounded hover:bg-[#F3F3F3]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[#A8A29E]" />
            <input
              data-testid="assign-staff-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-8 pr-3 py-2 border border-[#E5E5E5] rounded-lg text-xs text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]"
            />
          </div>

          {/* Staff list */}
          <div className="max-h-52 overflow-y-auto space-y-1" data-testid="assign-staff-list">
            {loading && <p className="text-xs text-[#706E6B] py-3 text-center">Loading staff…</p>}
            {!loading && filtered.length === 0 && (
              <p className="text-xs text-[#706E6B] py-3 text-center">No staff found</p>
            )}
            {filtered.map((u) => {
              const isCurrentAssignee = u.id === currentAssigneeId;
              return (
                <button
                  key={u.id}
                  data-testid={`assign-staff-${u.id}`}
                  onClick={() => setSelected(u)}
                  disabled={isCurrentAssignee}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                    selected?.id === u.id
                      ? "border-[#0176D3] bg-[#EEF4FF]"
                      : isCurrentAssignee
                      ? "border-[#E5E5E5] bg-[#F9F9F9] opacity-50 cursor-not-allowed"
                      : "border-[#E5E5E5] hover:bg-[#F3F3F3]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <span className="font-medium text-[#181818]">{u.name || u.email}</span>
                      {u.name && <span className="text-[#706E6B] ml-1">{u.email}</span>}
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F4F0EA] text-[#57534E]">
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </div>
                  {isCurrentAssignee && (
                    <span className="text-[10px] text-[#0176D3] mt-0.5 block">Currently assigned</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Optional note */}
          <div>
            <label className="block text-[10px] font-medium text-[#706E6B] mb-1 uppercase tracking-wide">
              Note (optional)
            </label>
            <input
              data-testid="assign-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason for assignment…"
              className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-xs text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>
        <div className="flex gap-2 p-4 border-t border-[#E5E5E5]">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-xs text-[#706E6B] border border-[#E5E5E5] rounded-lg hover:bg-[#F3F3F3] transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="assign-confirm-btn"
            onClick={handleAssign}
            disabled={!selected || submitting}
            className="flex-1 px-3 py-2 text-xs bg-[#0176D3] text-white rounded-lg hover:bg-[#005FB2] disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
          >
            {submitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
            {submitting ? "Assigning…" : `Assign${selected ? ` to ${selected.name || selected.email}` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Inbox item detail panel ───────────────────────────────────────────────────
function InboxItemDetail({ item, onBack, onResolved, currentUser }) {
  const [detail, setDetail]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [showLinkLead, setShowLinkLead]     = useState(false);
  const [showAssignTo, setShowAssignTo]     = useState(false);
  const [actionLoading, setActionLoading]   = useState(false);
  const [actionMsg, setActionMsg]           = useState(null);
  const [history, setHistory]         = useState(null);   // Phase B: assignment history

  const token = localStorage.getItem("token");

  const load = useCallback(() => {
    fetch(`${API}/api/communication/inbox/${item.id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setDetail(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [item.id, token]);

  useEffect(() => { load(); }, [load]);

  // Phase B: mark as read when detail panel opens (actual authorized viewing)
  useEffect(() => {
    if (!item.id) return;
    fetch(`${API}/api/communication/inbox/${item.id}/read`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify({}),
    }).catch(() => {});   // fire-and-forget; failure is non-fatal
  }, [item.id, token]);

  // Phase B: fetch assignment history after detail loads (or re-assign)
  useEffect(() => {
    if (!detail) return;
    fetch(`${API}/api/communication/inbox/${item.id}/history?limit=20`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setHistory(d); })
      .catch(() => {});
  }, [item.id, token, detail?.assigned_user_id]);

  const handleIgnore = async () => {
    setActionLoading(true);
    try {
      const r = await fetch(
        `${API}/api/communication/inbox/${item.id}/ignore`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reason: "SPAM" }),
          credentials: "include",
        }
      );
      if (!r.ok) throw new Error("Failed to ignore");
      setActionMsg({ type: "success", text: "Conversation marked as spam." });
      setTimeout(() => onResolved(), 1500);
    } catch (e) {
      setActionMsg({ type: "error", text: e.message || "Failed" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!currentUser) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      const r = await fetch(`${API}/api/communication/inbox/${item.id}/assign`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ assignee_id: currentUser.id }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        if (r.status === 409) {
          setActionMsg({ type: "error", text: "This conversation was claimed by another agent. Refreshing…" });
          setTimeout(() => load(), 1500);
          return;
        }
        throw new Error(e.detail || "Claim failed");
      }
      const data = await r.json();
      setActionMsg({ type: "success", text: `Conversation claimed. Assigned to you.` });
      load(); // refresh detail
    } catch (e) {
      setActionMsg({ type: "error", text: e.message || "Claim failed" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnassign = async () => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      const r = await fetch(`${API}/api/communication/inbox/${item.id}/unassign`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Unassign failed");
      }
      setActionMsg({ type: "success", text: "Conversation returned to unassigned pool." });
      load();
    } catch (e) {
      setActionMsg({ type: "error", text: e.message || "Unassign failed" });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#706E6B] text-sm">
        <Clock className="w-4 h-4 mr-2 animate-spin" />
        Loading conversation…
      </div>
    );
  }

  const d = detail || item;
  const isResolved = d.status === "RESOLVED" || d.status === "SPAM";

  return (
    <div data-testid="inbox-item-detail">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-[#0176D3] hover:underline mb-4"
        data-testid="inbox-detail-back-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to Inbox
      </button>

      {/* Header card */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-4 mb-4 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#EEF4FF] border border-[#C9DBF2] flex items-center justify-center">
              <Phone className="w-4 h-4 text-[#0176D3]" />
            </div>
            <div>
              <p className="font-mono text-base font-bold text-[#181818]" data-testid="inbox-phone-display">
                {d.phone_display || d.phone_normalized}
              </p>
              <p className="text-xs text-[#706E6B] mt-0.5">via WhatsApp · Mock Provider (B1/B2)</p>
              {d.first_contact_at && (
                <p className="text-xs text-[#706E6B] mt-0.5">
                  First contact: {formatDateTime(d.first_contact_at)}
                </p>
              )}
            </div>
          </div>
          <StatusBadge status={d.status} />
        </div>
        <div className="flex gap-3 mt-3 text-xs text-[#706E6B] ml-13">
          <span>{d.message_count || 0} messages</span>
          <span>·</span>
          <span>Last activity: {formatRelativeTime(d.last_activity_at)}</span>
        </div>
        {d.status === "RESOLUTION_REQUIRED" && d.candidate_lead_ids?.length > 0 && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-[#FFFDE7] border border-[#FFF176] rounded-lg text-xs text-[#F57F17]">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              <strong>{d.candidate_lead_ids.length} candidate lead{d.candidate_lead_ids.length !== 1 ? "s" : ""}</strong> found for this phone number.
              {" "}Use "Link Existing" to connect to the correct one.
            </span>
          </div>
        )}
      </div>

      {/* Assignment panel — WhatsApp items only */}
      {(() => {
        const assignee   = d.assignee;
        const canClaim   = hasCapability(currentUser, "comms.channel.read") && !assignee && !isResolved;
        const canAssign  = hasCapability(currentUser, "comms.channel.manage") && !isResolved;
        const canUnassign = hasCapability(currentUser, "comms.channel.manage") && assignee && !isResolved;
        return (
          <div
            className="bg-white border border-[#E5E5E5] rounded-xl p-4 mb-4 shadow-sm"
            data-testid="assignment-panel"
          >
            <h4 className="text-xs font-semibold text-[#444444] uppercase tracking-wide mb-3">Assignment</h4>
            {assignee ? (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#EEF4FF] border border-[#C9DBF2] flex items-center justify-center flex-shrink-0">
                    <UserCheck className="w-3.5 h-3.5 text-[#0176D3]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#181818]" data-testid="assignee-name">
                      {assignee.name || assignee.email}
                    </p>
                    <p className="text-xs text-[#706E6B]" data-testid="assignee-role">{assignee.role}</p>
                    {assignee.email && assignee.name && (
                      <p className="text-[10px] text-[#A8A29E]">{assignee.email}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {canAssign && (
                    <button
                      data-testid="action-reassign-btn"
                      onClick={() => setShowAssignTo(true)}
                      disabled={actionLoading}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-[#0176D3] border border-[#0176D3] rounded-lg hover:bg-[#EEF4FF] transition-colors disabled:opacity-50"
                    >
                      <Users className="w-3 h-3" /> Reassign
                    </button>
                  )}
                  {canUnassign && (
                    <button
                      data-testid="action-unassign-btn"
                      onClick={handleUnassign}
                      disabled={actionLoading}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-[#706E6B] border border-[#E5E5E5] rounded-lg hover:text-red-600 hover:border-red-300 transition-colors disabled:opacity-50"
                    >
                      <UserMinus className="w-3 h-3" />
                      {actionLoading ? "…" : "Unassign"}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-[#706E6B]">
                  <div className="w-8 h-8 rounded-full bg-[#F3F3F3] border border-[#E5E5E5] flex items-center justify-center flex-shrink-0">
                    <Users className="w-3.5 h-3.5 text-[#A8A29E]" />
                  </div>
                  <span data-testid="assignee-unassigned">Unassigned</span>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {canClaim && (
                    <button
                      data-testid="action-claim-btn"
                      onClick={handleClaim}
                      disabled={actionLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0176D3] text-white rounded-lg hover:bg-[#005FB2] disabled:opacity-50 transition-colors"
                    >
                      {actionLoading
                        ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        : <UserCheck className="w-3.5 h-3.5" />}
                      {actionLoading ? "Claiming…" : "Claim"}
                    </button>
                  )}
                  {canAssign && (
                    <button
                      data-testid="action-assign-to-btn"
                      onClick={() => setShowAssignTo(true)}
                      disabled={actionLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#0176D3] text-[#0176D3] rounded-lg hover:bg-[#EEF4FF] disabled:opacity-50 transition-colors"
                    >
                      <Users className="w-3.5 h-3.5" /> Assign to…
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Phase B: Assignment History panel */}
      {history && history.total > 0 && (
        <div
          className="bg-white border border-[#E5E5E5] rounded-xl p-4 mb-4 shadow-sm"
          data-testid="assignment-history-panel"
        >
          <h4 className="text-xs font-semibold text-[#444444] uppercase tracking-wide mb-3">
            Assignment History
            <span className="ml-2 text-[10px] font-normal text-[#A8A29E] normal-case">
              ({history.total} event{history.total !== 1 ? "s" : ""})
            </span>
          </h4>
          <div className="space-y-2">
            {history.history.map((evt) => {
              const ACTION_LABELS = {
                INBOX_ITEM_CLAIM:           { label: "Claimed",        color: "text-[#2EA44F]",  bg: "bg-[#F0FFF4]"  },
                INBOX_ITEM_ASSIGN:          { label: "Assigned",       color: "text-[#0176D3]",  bg: "bg-[#EEF4FF]"  },
                INBOX_ITEM_REASSIGN:        { label: "Reassigned",     color: "text-[#8B5CF6]",  bg: "bg-[#F5F0FF]"  },
                INBOX_ITEM_UNASSIGN:        { label: "Unassigned",     color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]"  },
                INBOX_ITEM_ASSIGN_VIA_LEAD: { label: "Assigned via Lead", color: "text-[#0176D3]", bg: "bg-[#EEF4FF]" },
              };
              const style = ACTION_LABELS[evt.action] || { label: evt.action, color: "text-[#706E6B]", bg: "bg-[#F3F3F3]" };
              const ts    = evt.timestamp ? new Date(evt.timestamp).toLocaleString() : "—";
              return (
                <div
                  key={evt.event_id}
                  className="flex items-start gap-2.5"
                  data-testid={`history-event-${evt.event_id}`}
                >
                  <div className={`flex-shrink-0 mt-0.5 px-2 py-0.5 rounded text-[10px] font-semibold ${style.color} ${style.bg}`}>
                    {style.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#181818] truncate">
                      {evt.actor_name || evt.actor_email || evt.actor_id || "System"}
                      {evt.actor_role && (
                        <span className="text-[#A8A29E] ml-1">· {evt.actor_role}</span>
                      )}
                      {evt.actor_role_note && (
                        <span className="text-[#C9C9C9] ml-1 italic">(historical actor)</span>
                      )}
                    </p>
                    <p className="text-[10px] text-[#A8A29E]">{ts}</p>
                    {evt.summary && (
                      <p className="text-[10px] text-[#706E6B] mt-0.5 break-words">{evt.summary}</p>
                    )}
                  </div>
                </div>
              );
            })}
            {history.has_more && (
              <p className="text-[10px] text-[#A8A29E] text-center pt-1">
                + {history.total - history.history.length} more events — use the history API for full log
              </p>
            )}
          </div>
        </div>
      )}

      {/* Conversation */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-4 mb-4 shadow-sm">
        <h4 className="text-xs font-semibold text-[#444444] uppercase tracking-wide mb-3">Conversation</h4>
        <InboxMessageThread messages={d.messages} />
      </div>

      {/* Action feedback */}
      {actionMsg && (
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-3 ${
            actionMsg.type === "success"
              ? "bg-[#EEF9EE] text-[#2E7D32] border border-[#A5D6A7]"
              : "bg-red-50 text-red-600 border border-red-200"
          }`}
        >
          {actionMsg.type === "success" ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          {actionMsg.text}
        </div>
      )}

      {/* Actions */}
      {!isResolved && (
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="action-create-lead-btn"
            onClick={() => setShowCreateLead(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#0176D3] hover:bg-[#005FB2] text-white text-sm rounded-lg transition-colors shadow-sm"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Create Lead
          </button>
          <button
            data-testid="action-link-lead-btn"
            onClick={() => setShowLinkLead(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-white text-[#0176D3] text-sm rounded-lg transition-colors shadow-sm border border-[#0176D3] hover:bg-[#EEF4FF]"
          >
            <Link className="w-3.5 h-3.5" />
            Link Existing
          </button>
          <button
            data-testid="action-ignore-btn"
            onClick={handleIgnore}
            disabled={actionLoading}
            className="flex items-center gap-1.5 px-4 py-2 bg-white text-[#706E6B] hover:text-red-600 text-sm rounded-lg transition-colors shadow-sm border border-[#E5E5E5] hover:border-red-300 disabled:opacity-50"
          >
            <Ban className="w-3.5 h-3.5" />
            {actionLoading ? "Working…" : "Ignore / Spam"}
          </button>
        </div>
      )}

      {isResolved && d.resolution_notes && (
        <div className="bg-[#EEF9EE] border border-[#A5D6A7] rounded-lg px-3 py-2 text-xs text-[#2E7D32] flex items-start gap-2">
          <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {d.resolution_notes}
        </div>
      )}

      {showCreateLead && (
        <CreateLeadModal
          item={d}
          onClose={() => setShowCreateLead(false)}
          onSuccess={(result) => {
            setShowCreateLead(false);
            setActionMsg({
              type: "success",
              text: `Lead ${result.lead_number} created. Journey ID: ${result.journey_id}`,
            });
            setTimeout(() => onResolved(), 2500);
          }}
        />
      )}

      {showLinkLead && (
        <LinkLeadModal
          item={d}
          onClose={() => setShowLinkLead(false)}
          onSuccess={(result, lead) => {
            setShowLinkLead(false);
            setActionMsg({
              type: "success",
              text: `Linked to ${lead.lead_number} — ${lead.customer_name}. Journey: ${result.journey_id}`,
            });
            setTimeout(() => onResolved(), 2500);
          }}
        />
      )}

      {showAssignTo && (
        <AssignToModal
          itemId={item.id}
          currentAssigneeId={d.assigned_user_id}
          token={token}
          onClose={() => setShowAssignTo(false)}
          onSuccess={(data) => {
            setShowAssignTo(false);
            setActionMsg({
              type: "success",
              text: `Conversation assigned to ${data.assignee?.name || data.assignee?.email || "team member"}.`,
            });
            load();
          }}
        />
      )}
    </div>
  );
}

// ── Unified Inbox item card (all channels) ───────────────────────────────────
function InboxItemCard({ item, onClick }) {
  const ch  = CHANNEL_CONFIG[item.channel] || CHANNEL_CONFIG.WEB;
  const { Icon } = ch;
  const isUnread = item.is_read_by_me === false;  // strict check — null/undefined = no dot

  return (
    <button
      data-testid={`inbox-item-${item.id}`}
      onClick={() => onClick(item)}
      className={`w-full text-left border rounded-xl p-4 transition-all group shadow-sm hover:shadow relative ${
        isUnread
          ? "bg-[#FAFCFF] border-[#C9DBF2] hover:bg-[#EEF4FF]"
          : "bg-white border-[#E5E5E5] hover:bg-[#FAFAF9]"
      }`}
    >
      {/* Phase B: Unread dot */}
      {isUnread && (
        <span
          className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#0176D3]"
          data-testid={`card-unread-dot-${item.id}`}
          title="New / unread"
        />
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ background: ch.bg, border: `1px solid ${ch.color}30` }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: ch.color }} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-sm font-semibold text-[#181818] truncate max-w-[200px]">
                {item.sender_display}
              </p>
              {item.inquiry_number && (
                <span className="text-[10px] text-[#A8A29E] font-mono">#{item.inquiry_number}</span>
              )}
            </div>
            <p className="text-xs text-[#706E6B] mt-0.5 line-clamp-1">
              {item.preview || "(No preview)"}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <StatusBadge status={item.status} />
          <span className="text-[10px] text-[#C9C9C9]">{formatRelativeTime(item.last_activity_at)}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2 ml-12 text-xs text-[#706E6B] flex-wrap">
        {item.channel === "WHATSAPP" && item.sender_phone && (
          <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{item.sender_phone}</span>
        )}
        {item.channel === "EMAIL" && item.sender_email && (
          <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{item.sender_email}</span>
        )}
        {item.channel === "WHATSAPP" && (
          <span className="flex items-center gap-1">
            <MessageCircle className="w-3 h-3" />
            {item.message_count} msg{item.message_count !== 1 ? "s" : ""}
          </span>
        )}
        <span>·</span>
        <ProviderModeBadge mode={item.provider_mode} />
        {item.lead_id && (
          <span className="text-green-600 flex items-center gap-0.5">
            <CheckCircle className="w-3 h-3" />Lead linked
          </span>
        )}
        {item.assigned_user_id && (
          <span className="flex items-center gap-0.5 text-[#0176D3]" data-testid={`card-assigned-${item.id}`}>
            <UserCheck className="w-3 h-3" />Claimed
          </span>
        )}
        <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </button>
  );
}

// ── Inquiry detail panel (WEB / EMAIL items) ─────────────────────────────────
function InquiryItemDetail({ item, onBack }) {
  const ch = CHANNEL_CONFIG[item.channel] || CHANNEL_CONFIG.WEB;
  return (
    <div data-testid="inquiry-item-detail">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-[#0176D3] hover:underline mb-4"
        data-testid="inquiry-detail-back-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to Inbox
      </button>

      <div className="bg-white border border-[#E5E5E5] rounded-xl p-4 mb-4 shadow-sm space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: ch.bg }}>
              <ch.Icon className="w-4 h-4" style={{ color: ch.color }} />
            </div>
            <div>
              <p className="text-base font-bold text-[#181818]" data-testid="inquiry-sender-display">
                {item.sender_display}
              </p>
              {item.inquiry_number && (
                <p className="text-xs text-[#706E6B] font-mono">{item.inquiry_number}</p>
              )}
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-[#706E6B]">via {ch.label}</span>
                <ProviderModeBadge mode={item.provider_mode} />
              </div>
            </div>
          </div>
          <StatusBadge status={item.status} />
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          {item.sender_email && (
            <div>
              <p className="text-[#A8A29E] uppercase tracking-wide text-[10px]">Email</p>
              <p className="text-[#181818]">{item.sender_email}</p>
            </div>
          )}
          {item.sender_phone && (
            <div>
              <p className="text-[#A8A29E] uppercase tracking-wide text-[10px]">Phone</p>
              <p className="text-[#181818]">{item.sender_phone}</p>
            </div>
          )}
          {item.first_contact_at && (
            <div>
              <p className="text-[#A8A29E] uppercase tracking-wide text-[10px]">Received</p>
              <p className="text-[#181818]">{formatDateTime(item.first_contact_at)}</p>
            </div>
          )}
          {item.lead_id && (
            <div>
              <p className="text-[#A8A29E] uppercase tracking-wide text-[10px]">Lead</p>
              <a href={`/engage/leads/${item.lead_id}`} className="text-[#0176D3] hover:underline text-xs flex items-center gap-1">
                View Lead <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>

        {item.preview && (
          <div className="bg-[#F4F0EA] rounded-lg p-3">
            <p className="text-[10px] text-[#A8A29E] uppercase tracking-wide mb-1">Message Preview</p>
            <p className="text-xs text-[#1C1917] whitespace-pre-wrap">{item.preview}</p>
          </div>
        )}
      </div>

      {item.channel === "EMAIL" && (
        <div className="p-3 rounded-xl text-xs" style={{ background: "#FDF2F5", border: "1px solid #C9A15F" }}>
          <strong style={{ color: "#7A1A2B" }}>UAT Mode:</strong>
          <span className="ml-1" style={{ color: "#7A1A2B" }}>Email delivery is restricted to the allowlist. Not a live inbox reply.</span>
        </div>
      )}
    </div>
  );
}

// ── Main Communication Inbox page ─────────────────────────────────────────────
export default function CommunicationInbox() {
  const [activeChannel, setActiveChannel] = useState("ALL");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(1);
  const [currentUser, setCurrentUser] = useState(null);   // OMNI-INBOX-02

  const token = localStorage.getItem("token");

  // Fetch current user once for capability checks (cookie-based auth — no token guard)
  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API}/api/auth/me`, { headers, credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => { if (u) setCurrentUser(u); })
      .catch(() => {});
  }, [token]);

  const load = useCallback(() => {
    const params = new URLSearchParams({ channel: activeChannel, page, limit: 20 });
    setLoading(true);
    fetch(`${API}/api/communication/unified-inbox?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { setData(d); setError(null); })
      .catch(() => setError("Failed to load inbox. Please refresh."))
      .finally(() => setLoading(false));
  }, [activeChannel, page, token]);

  useEffect(() => { setPage(1); }, [activeChannel]);
  useEffect(() => { load(); }, [load]);

  const handleChannelChange = (ch) => {
    setActiveChannel(ch);
    setSelectedItem(null);
  };

  // ── Selected item detail ────────────────────────────────────────────────────
  if (selectedItem) {
    const isWA = selectedItem.channel === "WHATSAPP";
    return (
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          {isWA ? (
            <InboxItemDetail
              item={selectedItem}
              onBack={() => { setSelectedItem(null); load(); }}   // Phase B: reload to refresh is_read_by_me
              onResolved={() => { setSelectedItem(null); load(); }}
              currentUser={currentUser}
            />
          ) : (
            <InquiryItemDetail
              item={selectedItem}
              onBack={() => { setSelectedItem(null); load(); }}   // Phase B: reload on back
            />
          )}
        </div>
      </div>
    );
  }

  const counts      = data?.channel_counts || {};
  const pagination  = data?.pagination     || {};
  const channelModes = data?.channel_modes  || {};

  const CHANNEL_TABS = [
    { key: "ALL",      label: "All",      icon: MessageCircle },
    { key: "WHATSAPP", label: "WhatsApp", icon: MessageSquare },
    { key: "EMAIL",    label: "Email",    icon: Mail          },
    { key: "WEB",      label: "Web",      icon: Globe         },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6" data-testid="communication-inbox-page">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle className="w-5 h-5" style={{ color: "#7A1A2B" }} />
              <h1 className="text-xl font-bold" style={{ color: "#1C1917" }}>
                Communication Inbox
              </h1>
            </div>
            <p className="text-sm" style={{ color: "#78716C" }}>
              Unified view of WhatsApp, Email and Website enquiries
            </p>
          </div>
          <button
            onClick={load}
            data-testid="inbox-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition-colors"
            style={{ color: "#78716C", borderColor: "#E5E0D8", background: "#FAF6F0" }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Channel tabs */}
        <div
          className="flex gap-1 p-1 rounded-xl mb-4 overflow-x-auto"
          style={{ background: "#F4F0EA" }}
          data-testid="channel-tabs"
        >
          {CHANNEL_TABS.map(({ key, label, icon: TabIcon }) => {
            const isActive = activeChannel === key;
            const cnt = key === "ALL" ? (counts.ALL || 0) : (counts[key] || 0);
            const mode = key === "ALL" ? null : channelModes[key];
            return (
              <button
                key={key}
                data-testid={`channel-tab-${key.toLowerCase()}`}
                onClick={() => handleChannelChange(key)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0"
                style={{
                  background: isActive ? "#FFFFFF" : "transparent",
                  color: isActive ? "#1C1917" : "#78716C",
                  boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                }}
              >
                <TabIcon className="w-3.5 h-3.5" />
                {label}
                <span
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{
                    background: isActive ? "#F4F0EA" : "#E5E0D8",
                    color: isActive ? "#1C1917" : "#A8A29E",
                  }}
                >
                  {cnt}
                </span>
                {mode && key !== "ALL" && (
                  <ProviderModeBadge mode={mode} />
                )}
              </button>
            );
          })}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16 text-sm" style={{ color: "#78716C" }} data-testid="inbox-loading">
            <Clock className="w-4 h-4 mr-2 animate-spin" />
            Loading inbox…
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="flex items-center gap-2 text-sm p-4 rounded-xl" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="inbox-error">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Content */}
        {!loading && !error && data && (
          <>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs" style={{ color: "#78716C" }} data-testid="inbox-total">
                {pagination.total || 0} conversation{(pagination.total || 0) !== 1 ? "s" : ""}
                {pagination.pages > 1 && ` · Page ${page} of ${pagination.pages}`}
              </span>
            </div>

            {(pagination.total || 0) === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3" style={{ color: "#78716C" }} data-testid="inbox-empty">
                <MessageCircle className="w-12 h-12" style={{ color: "#D6D3D1" }} />
                <p className="text-sm font-medium">No conversations in this category</p>
                {activeChannel !== "ALL" && (
                  <p className="text-xs" style={{ color: "#A8A29E" }}>
                    {activeChannel === "EMAIL"    ? "No email enquiries received yet (UAT mode)"   : ""}
                    {activeChannel === "WHATSAPP" ? "No WhatsApp conversations yet (Mock provider)" : ""}
                    {activeChannel === "WEB"      ? "No web enquiries yet"                          : ""}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2" data-testid="inbox-items-list">
                {(data.items || []).map((item) => (
                  <InboxItemCard key={item.id} item={item} onClick={setSelectedItem} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-5 pt-4 border-t" style={{ borderColor: "#E5E0D8" }}>
                <button
                  data-testid="inbox-prev-page"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs rounded-lg border disabled:opacity-40 transition-colors"
                  style={{ borderColor: "#E5E0D8", color: "#57534E" }}
                >
                  ← Prev
                </button>
                <span className="text-xs" style={{ color: "#78716C" }}>
                  {page} / {pagination.pages}
                </span>
                <button
                  data-testid="inbox-next-page"
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1.5 text-xs rounded-lg border disabled:opacity-40 transition-colors"
                  style={{ borderColor: "#E5E0D8", color: "#57534E" }}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
